import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import DeleteIcon from '@mui/icons-material/Delete'
import DescriptionIcon from '@mui/icons-material/Description'
import GetAppIcon from '@mui/icons-material/GetApp'
import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import { useDownloadAttachment } from 'api/aws/comments/useDownloadAttachments'
import React, { useState } from 'react'
import { Attachments, Task } from 'types'
import PictureAsPdfIcon from '../../images/icons/PDF_file_icon.svg'
import TaskImageUploader from './TaskImageUploader'

interface ThumbnailProps {
  task: Task
  onDeleteFile: (url: string) => void
  handleDownload: (url: string) => void
  onOpenViewer: (attachments: Attachments[], index: number) => void
}

const getFileNameFromUrl = (url: string): string => {
  try {
    const lastSegment = url.split('/').pop() || ''
    const match = lastSegment.match(/_\d+-([^/]+)$/)
    return match?.[1] || lastSegment
  } catch {
    return 'unknown-file'
  }
}

const getThumbnail = (url: string) => {
  if (/\.(svg|png|jpe?g|gif)$/i.test(url)) {
    return (
      <Box
        component="img"
        src={url}
        alt="task-file"
        sx={{
          width: '100%',
          height: '100%',
          maxHeight: 35,
          borderRadius: 1,
          objectFit: 'scale-down',
          transition: 'transform 0.2s ease',
        }}
      />
    )
  } else if (/\.pdf$/i.test(url)) {
    return <img src={PictureAsPdfIcon} alt="PDF Icon" style={{ height: 35, width: 35, objectFit: 'cover' }} />
  } else {
    return <DescriptionIcon sx={{ fontSize: 35, color: 'text.secondary' }} />
  }
}

const PreviewTaskAttachments: React.FC<ThumbnailProps> = ({ task, onDeleteFile, handleDownload, onOpenViewer }) => {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null)
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}')
  const taskOwnerId = task?.owner || task?.owner?.[0]
  const isTaskCreator = currentUser?.user_uuid === taskOwnerId
  const { downloadAllAttachments } = useDownloadAttachment()
  const imageUrls = Array.from(new Set(task?.imageUrls ?? []))
  const attachments: Attachments[] = (imageUrls ?? []).map((url, index) => ({
    id: `${index}`,
    iconLink: url,
    mimeType: url.endsWith('.pdf') ? 'application/pdf' : 'image/*',
    name: getFileNameFromUrl(url),
    thumbnailLink: url,
    url,
  }))

  const handleClickDelete = (event: React.MouseEvent, url: string) => {
    event.stopPropagation()
    setSelectedUrl(url)
    setAnchorEl(event.currentTarget as HTMLElement)
  }

  const handleConfirmDelete = () => {
    if (selectedUrl) {
      onDeleteFile(selectedUrl)
      handleClosePopover()
    }
  }

  const handleClosePopover = () => {
    setAnchorEl(null)
    setSelectedUrl(null)
  }

  const handleThumbnailClick = (index: number) => {
    onOpenViewer(attachments, index)
  }

  return (
    <Box
      sx={{
        borderTop: `1px solid ${theme.palette.divider}`,
        px: 1,
        py: 0.5, // reduce vertical padding
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minHeight: 48, // compact bar
        maxHeight: 64,
        overflowX: 'auto',
        flexShrink: 0,
        zIndex: 2000,
      }}
    >
      {!attachments?.length && (
        <Typography sx={{ color: 'text.disabled', fontSize: 12 }}>Upload attachments / images</Typography>
      )}
      <TaskImageUploader task={task} />

      {attachments.map((attachment, index) => (
        <Box
          key={`${index}-${attachment.url}`}
          sx={{
            width: 60,
            height: 45,
            flexShrink: 0,
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            cursor: 'pointer',
            overflow: 'hidden',
            '&:hover .overlay': { opacity: 1 },
          }}
          onClick={() => onOpenViewer(attachments, index)}
        >
          <Tooltip
            key={attachment.url}
            placement="top"
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                  {attachment.name}
                </Typography>
                {isTaskCreator && (
                  <IconButton
                    size="small"
                    sx={{ color: '#fff' }}
                    onClick={e => {
                      e.stopPropagation()
                      onDeleteFile(attachment.url)
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}

                <IconButton
                  size="small"
                  sx={{ color: '#fff' }}
                  onClick={e => {
                    e.stopPropagation()
                    handleDownload(attachment.url)
                  }}
                >
                  <GetAppIcon fontSize="small" />
                </IconButton>
              </Box>
            }
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'background.default',
                  color: 'text.primary',
                  p: 1,
                  borderRadius: 1,
                },
              },
            }}
          >
            {getThumbnail(attachment.url)}
          </Tooltip>
          {/* <Box
            className="overlay"
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'none',
              backgroundColor: theme.palette.background.defaultdim,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-evenly',
              opacity: 0,
              transition: 'opacity 0.2s',
            }}
          >
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation()
                onDeleteFile(attachment.url)
              }}
            >
              <CloseIcon fontSize="small" sx={{ color: 'white' }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation()
                handleDownload(attachment.url)
              }}
            >
              <GetAppIcon fontSize="small" sx={{ color: 'white' }} />
            </IconButton>
          </Box> */}
        </Box>
      ))}

      {attachments.length > 1 && (
        <Tooltip title="Download All as ZIP">
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation()
              downloadAllAttachments(attachments, `${task.name}_attachments.zip`)
            }}
          >
            <CloudDownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  )
}

export default PreviewTaskAttachments
